import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import Img from "gatsby-image"
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from "gatsby-source-contentful/rich-text"
import React from "react"
import styled, { css } from "styled-components"
import Breakpoints from "../../../components/shared/breakpoints"
import { CheckMarkNoOutline } from "../../../components/shared/svg/check-mark"
import {
  ContentfulResponsiveImage,
  IContentfulResponsiveImage,
  RESPONSIVE_IMAGE_TYPENAME,
} from "../../atoms/breakpoint-image"
import {
  BUTTON_TYPENAME,
  ContentfulButton,
  IContentfulButton,
} from "../../atoms/button"
import { Text } from "../../atoms/text"
import VariantContainer from "../../atoms/variant-container"
import ContentBlock from "../../molecules/content-block"
import { Alignments as IconTitleAlignments } from "../../molecules/icon-title"
import {
  ContentfulIconTitleCard,
  IContentfulIconTitleCard,
  ICON_TITLE_CARD_TYPENAME,
} from "../../molecules/icon-title-card"
import {
  ITestimonialCard,
  TESTIMONIAL_TYPENAME,
} from "../../molecules/testimonial-card"

const UnorderedList = styled.ul`
  :not(:first-child) {
    margin-top: 32px;
  }
  display: grid;
  justify-content: center;
  justify-self: center;
  gap: 16px 32px;
  @media (min-width: ${Breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
    grid-auto-flow: row;
  }
  @media (min-width: ${Breakpoints.lg}) {
    margin-top: 48px;
  }
`

const ListItem = styled.li`
  display: flex;
  align-items: center;
  text-align: left;

  svg {
    width: 30px;
    height: 30px;
    margin-right: 16px;
    flex-shrink: 0;
    display: inline-block;
    vertical-align: top;
    @media (min-width: ${Breakpoints.md}) {
      width: 24px;
      height: 24px;
    }
    @media (min-width: ${Breakpoints.lg}) {
      width: 30px;
      height: 30px;
    }
  }
`

const SideBySideText = ({
  text,
}: {
  text: RenderRichTextData<ContentfulRichTextGatsbyReference>
}) => {
  return (
    <VariantContainer
      variants={[
        css`
          margin-top: 32px;
          text-align: center;
        `,
        css`
          margin-top: 48px;
        `,
        css`
          text-align: left;
          margin: 0 48px;
        `,
      ]}
    >
      <ContentBlock.RichText
        options={{
          renderNode: {
            [BLOCKS.LIST_ITEM]: (node, children) => (
              <ListItem>
                <CheckMarkNoOutline color="var(--emerald)" />
                {children}
              </ListItem>
            ),
            [BLOCKS.UL_LIST]: (node, children) => (
              <UnorderedList>{children}</UnorderedList>
            ),
            [BLOCKS.EMBEDDED_ENTRY]: (node) => {
              const component:
                | ITestimonialCard
                | IContentfulButton
                | IContentfulResponsiveImage
                | IContentfulIconTitleCard = node?.data?.target

              if (component) {
                switch (component.__typename) {
                  case BUTTON_TYPENAME:
                    return (
                      <VariantContainer
                        variants={[
                          css`
                            display: grid;
                            justify-items: center;
                            margin-top: 32px;
                          `,
                          css`
                            margin-top: 48px;
                          `,
                          css`
                            justify-items: left;
                          `,
                        ]}
                      >
                        <ContentfulButton {...component} />
                      </VariantContainer>
                    )
                  case TESTIMONIAL_TYPENAME:
                    // Side by side renders the testimonial in a unique style from the regular card
                    return (
                      <>
                        <VariantContainer
                          variants={[
                            css`
                              display: flex;
                              justify-content: center;
                              & > * {
                                :not(:last-child) {
                                  margin-right: 32px;
                                }
                              }
                              margin-bottom: 24px;
                            `,
                            null,
                            css`
                              display: flex;
                              justify-content: flex-start;
                            `,
                          ]}
                        >
                          {Array.isArray(component?.logos) &&
                            component?.logos.map((logo, index) => (
                              <VariantContainer
                                variants={[
                                  css`
                                    img,
                                    .gatsby-image-wrapper {
                                      max-width: 210px;
                                      height: 48px;
                                    }
                                  `,
                                  css`
                                    img,
                                    .gatsby-image-wrapper {
                                      max-width: 280px;
                                      height: 64px;
                                    }
                                  `,
                                ]}
                                key={index}
                              >
                                {logo?.fluid ? (
                                  <Img
                                    fluid={logo.fluid}
                                    alt={logo.description || ""}
                                    imgStyle={{ objectFit: "contain" }}
                                  />
                                ) : (
                                  <img
                                    src={logo.file.url}
                                    alt={logo.description || ""}
                                  />
                                )}
                              </VariantContainer>
                            ))}
                        </VariantContainer>
                        <ContentBlock.RichText
                          variants={[
                            css`
                              margin-bottom: 24px;
                            `,
                          ]}
                        >
                          {/* we know in this case it's coming from contentful and will be renderRichText type */}
                          {
                            component.quote as RenderRichTextData<ContentfulRichTextGatsbyReference>
                          }
                        </ContentBlock.RichText>
                        <Text.Body
                          variants={[
                            css`
                              color: var(--burnt-orange);
                              font-weight: 500;
                              font-size: 14px;
                              line-height: 150%;
                              letter-spacing: 0.08em;
                              text-transform: uppercase;
                            `,
                            css`
                              font-weight: 500;
                              font-size: 14px;
                              line-height: 150%;
                              letter-spacing: 0.08em;
                            `,
                            css`
                              font-weight: 500;
                              font-size: 16px;
                              line-height: 150%;
                              letter-spacing: 0.08em;
                            `,
                          ]}
                          as="cite"
                        >
                          {component.author}
                          {component.jobTitle && (
                            <>
                              <br />
                              {component.jobTitle}
                            </>
                          )}
                          {component.organization && (
                            <>
                              <br />
                              {component.organization}
                            </>
                          )}
                        </Text.Body>
                      </>
                    )
                  case RESPONSIVE_IMAGE_TYPENAME:
                    return <ContentfulResponsiveImage {...component} />
                  case ICON_TITLE_CARD_TYPENAME:
                    return (
                      <VariantContainer
                        variants={[
                          css`
                            :not(:first-child) {
                              margin-top: 48px;
                            }
                          `,
                          null,
                          css`
                            :first-of-type {
                              margin-top: 64px;
                            }
                          `,
                        ]}
                      >
                        <ContentfulIconTitleCard
                          titleAlignmentVariants={[
                            IconTitleAlignments["row-left"],
                            IconTitleAlignments["row-center"],
                            IconTitleAlignments["row-overhang"],
                          ]}
                          {...component}
                        />
                      </VariantContainer>
                    )
                  default:
                    return null
                }
              }
            },
            [INLINES.EMBEDDED_ENTRY]: (node) => {
              const component: IContentfulButton = node?.data?.target
              if (component) {
                switch (component.__typename) {
                  case BUTTON_TYPENAME:
                    return (
                      <ContentfulButton
                        {...component}
                        variants={[
                          css`
                            display: block;
                            margin: 0 auto;
                            :first-of-type {
                              margin-top: 32px;
                            }
                            :not(:first-of-type) {
                              margin-top: 24px;
                            }
                          `,
                          css`
                            display: inline-block;
                            :first-of-type {
                              margin-top: 48px;
                            }
                          `,
                          css`
                            :first-of-type {
                              margin-right: 24px;
                            }
                          `,
                        ]}
                      />
                    )
                  default:
                    return null
                }
              }
            },
          },
        }}
      >
        {text}
      </ContentBlock.RichText>
    </VariantContainer>
  )
}

export default SideBySideText
